import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { ScreenSchedule } from "../schemas/scheduleSchema";
import { screenPlaylistService } from "../api/services/schedularService";

export const useSchedular = () => {
  const queryClient = useQueryClient();

  const deleteAssignment = useMutation({
    mutationFn: screenPlaylistService.remove,
    onSuccess: () => {
      toast.success("Schedule deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["screen-schedules"] });
      queryClient.invalidateQueries({ queryKey: ["all-schedules"] });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete schedule");
    },
  });

  const deleteAssignmentForDate = useMutation({
    mutationFn: ({ id, date }: { id: string; date: string }) =>
      screenPlaylistService.removeForDate(id, date),
    onSuccess: () => {
      toast.success("Schedule removed for selected date");
      queryClient.invalidateQueries({ queryKey: ["screen-schedules"] });
      queryClient.invalidateQueries({ queryKey: ["all-schedules"] });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete schedule for date");
    },
  });

  const deleteScheduleBatch = useMutation({
    mutationFn: screenPlaylistService.removeBatch,
    onSuccess: () => {
      toast.success("Schedule deleted from all screens");
      queryClient.invalidateQueries({ queryKey: ["screen-schedules"] });
      queryClient.invalidateQueries({ queryKey: ["all-schedules"] });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete schedule batch");
    },
  });

  const deleteScheduleBatchForScreens = useMutation({
    mutationFn: ({ batchId, screenIds }: { batchId: string; screenIds: string[] }) =>
      screenPlaylistService.removeBatchForScreens(batchId, screenIds),
    onSuccess: () => {
      toast.success("Schedule deleted from selected screens");
      queryClient.invalidateQueries({ queryKey: ["screen-schedules"] });
      queryClient.invalidateQueries({ queryKey: ["all-schedules"] });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete schedule from selected screens");
    },
  });

  const deleteAllSchedulesForScreen = useMutation({
    mutationFn: screenPlaylistService.removeAllForScreen,
    onSuccess: () => {
      toast.success("All schedules deleted for this screen");
      queryClient.invalidateQueries({ queryKey: ["screen-schedules"] });
      queryClient.invalidateQueries({ queryKey: ["all-schedules"] });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete all schedules");
    },
  });

  const createSchedule = useMutation({
    mutationFn: screenPlaylistService.createSchedule,
    onSuccess: () => {
      toast.success("Schedule created successfully");
      queryClient.invalidateQueries({ queryKey: ["screen-schedules"] });
      queryClient.invalidateQueries({ queryKey: ["all-schedules"] });
      queryClient.invalidateQueries({ queryKey: ["screens"] });
    },
    onError: (error: any) => {
      const graphQLError = error?.graphQLErrors?.[0];
      if (graphQLError?.extensions?.code === "SCHEDULE_CONFLICT") {
        return;
      }
      toast.error(error.message || "Failed to create schedule");
    },
  });

  const useScreenSchedules = (screenId: string, date?: string) =>
    useQuery<ScreenSchedule[], Error>({
      queryKey: ["screen-schedules", screenId, date],
      queryFn: () => screenPlaylistService.getScreenSchedules(screenId, date),
      enabled: !!screenId,
      staleTime: 0,
      refetchOnMount: "always",
      refetchOnWindowFocus: true,
    });

  return {
    deleteAssignment: deleteAssignment.mutate,
    deleteAssignmentForDate: deleteAssignmentForDate.mutate,
    deleteScheduleBatch: deleteScheduleBatch.mutate,
    deleteScheduleBatchForScreens: deleteScheduleBatchForScreens.mutate,
    deleteAllSchedulesForScreen: deleteAllSchedulesForScreen.mutate,
    createSchedule: createSchedule.mutateAsync,
    useScreenSchedules,
  };
};
